import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { LoadingSpinner } from '@/shared/components';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string | string[];
}

export default function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is a referral recruit who needs to complete onboarding
  // Only applies to users with accountStatus === 'onboarding'
  const isReferralRecruit = user.accountStatus === 'onboarding';
  const onboardingStep = (user as any).onboardingStep;
  const isOnOnboardingPage = location.pathname.startsWith('/onboarding/');

  // If user is a referral recruit with pending onboarding and NOT on an onboarding page, redirect them
  if (isReferralRecruit && onboardingStep >= 3 && onboardingStep <= 6 && !isOnOnboardingPage) {
    return <Navigate to={`/onboarding/step-${onboardingStep}`} replace />;
  }

  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!allowedRoles.includes(user.role)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}
